/**
 * Pointer-event gesture tracking hook for voice recording.
 *
 * How it works:
 * 1. On pointerdown, captures the initial position and starts tracking.
 * 2. On pointermove (attached to window), computes deltaX/deltaY from origin.
 * 3. Horizontal drag left past SLIDE_CANCEL_THRESHOLD → fires onSlideCancel.
 * 4. Vertical drag up past LOCK_THRESHOLD → transitions to "locked" state.
 * 5. On pointerup, if not locked → fires onRelease; if locked → no-op (recording continues).
 *
 * Key features:
 * - Works with both touch and mouse via pointer events
 * - Exposes normalized slideProgress (0→1) and lockProgress (0→1) for animations
 * - Haptic feedback via navigator.vibrate() on threshold crossings
 * - Attaches move/up listeners to window so gestures work outside the button
 */

import { useRef, useState, useCallback, useEffect } from "react";

import { SLIDE_CANCEL_THRESHOLD, LOCK_THRESHOLD } from "@/lib/voice";

import type { GestureState } from "@/lib/voice";

/** Minimum drag distance (px) before the dominant axis is locked */
const AXIS_DEAD_ZONE = 10;

type DominantAxis = "horizontal" | "vertical" | null;

// ── Types ──

interface UseRecordingGestureOptions {
  /** Called when the user slides left past the cancel threshold */
  onSlideCancel: () => void;
  /** Called when the user slides up past the lock threshold */
  onLock: () => void;
  /** Called when the user releases without locking (normal stop) */
  onRelease: () => void;
  /** Whether gesture tracking is active (typically true when recording) */
  enabled: boolean;
}

interface UseRecordingGestureReturn {
  /** Current gesture state */
  gestureState: GestureState;
  /** 0→1 normalized horizontal distance toward cancel threshold */
  slideProgress: number;
  /** 0→1 normalized vertical distance toward lock threshold */
  lockProgress: number;
  /** Whether the recording is in locked (hands-free) mode */
  isLocked: boolean;
  /** Set the gesture origin — call this when recording starts and the pointer is still down */
  onPointerDown: (origin: { x: number; y: number }) => void;
  /** Reset gesture state (e.g. when recording ends) */
  reset: () => void;
  /** Immediately enter locked (hands-free) mode — used for desktop single-click */
  startLocked: () => void;
}

// ── Helpers ──

/** Safely trigger haptic feedback — no-ops on unsupported browsers */
function vibrate(pattern: number | number[]) {
  try {
    navigator.vibrate?.(pattern);
  } catch {
    // Vibration API not available — silently ignore
  }
}

interface SlideHandlers {
  setSlideProgress: (v: number) => void;
  setGestureState: (s: GestureState) => void;
  cancelledRef: React.MutableRefObject<boolean>;
  cleanupListenersRef: React.MutableRefObject<(() => void) | null>;
  onSlideCancelRef: React.MutableRefObject<() => void>;
}

/** Process horizontal slide-to-cancel. Returns true if threshold was crossed. */
function processHorizontalSlide(deltaX: number, h: SlideHandlers): boolean {
  if (deltaX > 0) {
    const progress = Math.min(deltaX / SLIDE_CANCEL_THRESHOLD, 1);
    h.setSlideProgress(progress);
    h.setGestureState(progress > 0.15 ? "sliding" : "holding");

    if (deltaX >= SLIDE_CANCEL_THRESHOLD) {
      h.cancelledRef.current = true;
      vibrate(50);
      h.cleanupListenersRef.current?.();
      h.onSlideCancelRef.current();
      return true;
    }
  } else {
    h.setSlideProgress(0);
    h.setGestureState("holding");
  }
  return false;
}

interface LockHandlers {
  setLockProgress: (v: number) => void;
  setGestureState: (s: GestureState) => void;
  lockedRef: React.MutableRefObject<boolean>;
  cleanupListenersRef: React.MutableRefObject<(() => void) | null>;
  onLockRef: React.MutableRefObject<() => void>;
}

/** Process vertical lock gesture. Returns true if threshold was crossed. */
function processVerticalLock(deltaY: number, h: LockHandlers): boolean {
  if (deltaY > 0) {
    const progress = Math.min(deltaY / LOCK_THRESHOLD, 1);
    h.setLockProgress(progress);

    if (deltaY >= LOCK_THRESHOLD) {
      h.lockedRef.current = true;
      vibrate([30, 30, 30]);
      h.cleanupListenersRef.current?.();
      h.setGestureState("locked");
      h.setLockProgress(1);
      h.onLockRef.current();
      return true;
    }
  } else {
    h.setLockProgress(0);
  }
  return false;
}

// ── Hook ──

export function useRecordingGesture({
  onSlideCancel,
  onLock,
  onRelease,
  enabled,
}: UseRecordingGestureOptions): UseRecordingGestureReturn {
  const [gestureState, setGestureState] = useState<GestureState>("idle");
  const [slideProgress, setSlideProgress] = useState(0);
  const [lockProgress, setLockProgress] = useState(0);

  // Origin point of the gesture
  const originRef = useRef<{ x: number; y: number } | null>(null);

  // Track whether thresholds have been crossed (to avoid re-firing)
  const cancelledRef = useRef(false);
  const lockedRef = useRef(false);

  // Dominant axis lock — prevents diagonal drag from triggering both gestures
  const dominantAxisRef = useRef<DominantAxis>(null);

  // Cleanup function for window listeners (set in onPointerDown)
  const cleanupListenersRef = useRef<(() => void) | null>(null);

  // Stable refs so callbacks always see latest values (avoids stale closures)
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;
  const onSlideCancelRef = useRef(onSlideCancel);
  onSlideCancelRef.current = onSlideCancel;
  const onLockRef = useRef(onLock);
  onLockRef.current = onLock;
  const onReleaseRef = useRef(onRelease);
  onReleaseRef.current = onRelease;

  const reset = useCallback(() => {
    cleanupListenersRef.current?.();
    originRef.current = null;
    cancelledRef.current = false;
    lockedRef.current = false;
    dominantAxisRef.current = null;
    setGestureState("idle");
    setSlideProgress(0);
    setLockProgress(0);
  }, []);

  // ── Pointer move handler ──

  const handlePointerMove = useCallback((e: PointerEvent) => {
    if (!originRef.current || cancelledRef.current || lockedRef.current) {
      return;
    }

    const deltaX = originRef.current.x - e.clientX; // positive = dragged left
    const deltaY = originRef.current.y - e.clientY; // positive = dragged up

    // Determine dominant axis after a small dead zone (prevents diagonal conflicts)
    if (!dominantAxisRef.current) {
      const absDx = Math.abs(deltaX);
      const absDy = Math.abs(deltaY);
      if (absDx >= AXIS_DEAD_ZONE || absDy >= AXIS_DEAD_ZONE) {
        dominantAxisRef.current = absDx >= absDy ? "horizontal" : "vertical";
      } else {
        return; // still inside dead zone
      }
    }

    if (dominantAxisRef.current === "horizontal") {
      processHorizontalSlide(deltaX, {
        setSlideProgress,
        setGestureState,
        cancelledRef,
        cleanupListenersRef,
        onSlideCancelRef,
      });
    } else {
      processVerticalLock(deltaY, {
        setLockProgress,
        setGestureState,
        lockedRef,
        cleanupListenersRef,
        onLockRef,
      });
    }
  }, []);

  // ── Pointer up handler ──

  const handlePointerUp = useCallback(() => {
    cleanupListenersRef.current?.();

    if (cancelledRef.current || lockedRef.current) {
      // Already handled by threshold crossing — just clean up listeners
      if (!lockedRef.current) {
        // Cancelled — full reset
        originRef.current = null;
        cancelledRef.current = false;
        setGestureState("idle");
        setSlideProgress(0);
        setLockProgress(0);
      }
      return;
    }

    // Normal release — stop recording & send
    onReleaseRef.current();
    originRef.current = null;
    setGestureState("idle");
    setSlideProgress(0);
    setLockProgress(0);
  }, []);

  // ── Pointer cancel handler ──
  //
  // iOS/iPadOS fires pointercancel after ~3-4 seconds of sustained touch
  // (the system takes over the pointer for text selection, the magnifier, etc.).
  // Treating this the same as pointerup would abruptly stop and send the
  // recording — instead, auto-lock into hands-free mode so the user can
  // continue recording and send when ready.

  const handlePointerCancel = useCallback(() => {
    cleanupListenersRef.current?.();

    if (cancelledRef.current) {
      // Slide-cancelled before pointercancel fired — full reset
      originRef.current = null;
      cancelledRef.current = false;
      setGestureState("idle");
      setSlideProgress(0);
      setLockProgress(0);
      return;
    }

    if (lockedRef.current) {
      // Already locked — pointercancel doesn't affect hands-free recording
      return;
    }

    // Auto-lock: recording continues hands-free
    lockedRef.current = true;
    vibrate([30, 30, 30]);
    originRef.current = null;
    setGestureState("locked");
    setLockProgress(1);
    onLockRef.current();
  }, []);

  // ── Start locked (desktop single-click) ──
  //
  // Directly enters locked (hands-free) mode without attaching any window
  // pointer listeners — there is no drag gesture to track on a mouse click.
  // The VoiceRecordingBar send/cancel buttons handle the rest.

  const startLocked = useCallback(() => {
    lockedRef.current = true;
    cancelledRef.current = false;
    originRef.current = null;
    setGestureState("locked");
    setSlideProgress(0);
    setLockProgress(1);
    onLockRef.current();
  }, []);

  // ── Cleanup listeners on unmount ──

  useEffect(() => {
    return () => cleanupListenersRef.current?.();
  }, []);

  // ── Reset when disabled (recording ended externally) ──

  useEffect(() => {
    if (!enabled) {
      reset();
    }
  }, [enabled, reset]);

  // ── Pointer down handler (attach to mic button) ──

  const onPointerDown = useCallback(
    (origin: { x: number; y: number }) => {
      if (!enabledRef.current) {
        return;
      }

      originRef.current = { x: origin.x, y: origin.y };
      cancelledRef.current = false;
      lockedRef.current = false;
      dominantAxisRef.current = null;

      // Attach listeners synchronously — the previous useEffect-based approach
      // created a timing gap where pointerup/pointercancel could fire before
      // React re-rendered and the effect ran. On iOS PWA (add-to-homescreen),
      // this caused ~90% of voice recordings to never send.
      cleanupListenersRef.current?.();
      globalThis.addEventListener("pointermove", handlePointerMove);
      globalThis.addEventListener("pointerup", handlePointerUp);
      globalThis.addEventListener("pointercancel", handlePointerCancel);

      cleanupListenersRef.current = () => {
        globalThis.removeEventListener("pointermove", handlePointerMove);
        globalThis.removeEventListener("pointerup", handlePointerUp);
        globalThis.removeEventListener("pointercancel", handlePointerCancel);
        cleanupListenersRef.current = null;
      };

      setGestureState("holding");
      setSlideProgress(0);
      setLockProgress(0);
    },
    [handlePointerMove, handlePointerUp, handlePointerCancel],
  );

  return {
    gestureState,
    slideProgress,
    lockProgress,
    isLocked: gestureState === "locked",
    onPointerDown,
    reset,
    startLocked,
  };
}
